import {Component, EventEmitter, Input, OnInit, Output} from '@angular/core';
import {AllergenGroup, DisplayedAllergenGroup} from "app/models/menu.model";
import * as MenuSelectors from "app/reducers/menu.selectors";
import {Store} from "@ngrx/store";
import {
  EditAllergenGroupModalComponent, EditAllergenGroupModalComponentDataType
} from "app/pages/menu/shared/edit-allergen-group-modal/edit-allergen-group-modal.component";
import {Organization} from "app/models/organization.model";
import {MatDialog} from "@angular/material/dialog";
import {setAllergenGroups} from "app/reducers/menu.actions";
import {AllergenGroupService} from "app/services/allergen-group.service";
import {TranslateService} from "@ngx-translate/core";
import {UntilDestroy, untilDestroyed} from "@ngneat/until-destroy";

@UntilDestroy()
@Component({
  selector: 'app-allergen-group',
  templateUrl: './allergen-group.component.html',
  styleUrls: ['./allergen-group.component.scss']
})
export class AllergenGroupComponent implements OnInit {
  @Input() allergenGroupIds: string[] | undefined;
  @Input() organization: Organization;
  @Input() menuSectionId: string;
  @Input() menuId: string;
  @Input() allowOverrides: boolean;

  @Output() allergenGroupIdsChange = new EventEmitter<string[]>()
  initValues = {};

  allergenGroups: DisplayedAllergenGroup[] = [];

  allergenGroupIdSet: Set<string> = new Set();

  allergenGroupsMap: Map<string, DisplayedAllergenGroup>;
  originalAllergenGroupsMap: Map<string, DisplayedAllergenGroup>;

  constructor(
    private allergenGroupService: AllergenGroupService,
    public dialog: MatDialog,
    private translate: TranslateService,
    private store: Store,
  ) { }

  ngOnInit(): void {
    this.allergenGroupIdSet = new Set(this.allergenGroupIds);
    this.store.select(MenuSelectors.selectAllergenGroups)
      .pipe(untilDestroyed(this)).subscribe(allergenGroups => {
        this.allergenGroups = [...allergenGroups].sort((a, b) => a.name.localeCompare(b.name));
      });
    this.store.select(MenuSelectors.selectDictionaryAllergenGroup)
      .pipe(untilDestroyed(this)).subscribe(allergenGroups => {
        this.allergenGroupsMap = new Map<string, DisplayedAllergenGroup>();
        for (const kvp of allergenGroups) {
          this.allergenGroupsMap.set(kvp[0], kvp[1]);
        }
      });
    this.store.select(MenuSelectors.selectOriginalDictionaryAllergenGroup)
      .pipe(untilDestroyed(this)).subscribe(allergenGroups => {this.originalAllergenGroupsMap = allergenGroups});
    this.initValues['allergenGroups'] = this.allergenGroupIds?.map(x => x);
  }

  getAllergenName(allergenGroupId: string){
    return this.allergenGroupsMap?.get(allergenGroupId)?.name ?? allergenGroupId;
  }

  onRemoveAllergenGroupFromProduct(allergenGroupId: string) {
    if(!this.allowOverrides){
      return;
    }
    if(this.allergenGroupIdSet.delete(allergenGroupId)){
        this.allergenGroupIdsChange.emit([...this.allergenGroupIdSet]);
      }
  }

  onAllergenGroupPicked(id: string) {
    if(!this.allowOverrides){
      return;
    }
    if(!this.allergenGroupIdSet.has(id)) {
      this.allergenGroupIdSet.add(id);
      this.allergenGroupIdsChange.emit([...this.allergenGroupIdSet]);
    }
  }

  onOpenAllergenGroup(allergenGroupId?: string) {
    if(!this.allowOverrides){
      return;
    }

    if(allergenGroupId && !this.getAllergenName(allergenGroupId)){
      return;
    }
    this.dialog.open(EditAllergenGroupModalComponent, {
      data: {
        allergenGroupId: allergenGroupId,
        allergenGroup: allergenGroupId ? this.allergenGroupsMap.get(allergenGroupId) as AllergenGroup : {},
        originalAllergenGroup: this.originalAllergenGroupsMap.get(allergenGroupId) ?? this.allergenGroupsMap.get(allergenGroupId) ?? {},
        organization: this.organization,
        menuSectionId: this.menuSectionId,
        menuId: this.menuId,
        allowOverrides: this.allowOverrides,
        allergenGroups: [...this.allergenGroupsMap.values()]
      } as EditAllergenGroupModalComponentDataType
    },).afterClosed().subscribe(result => {
      if(result.created){
        const created = result.created as DisplayedAllergenGroup;
        this.allergenGroupsMap.set(created.id, created);
        this.store.dispatch(setAllergenGroups([...this.allergenGroupsMap.values()]));
        this.onAllergenGroupPicked(created.id);
      } else if(result.updated){
        const updated = result.updated as DisplayedAllergenGroup;
        this.allergenGroupsMap.set(updated.id, updated);
        this.store.dispatch(setAllergenGroups([...this.allergenGroupsMap.values()]))
      } else if(result.deleted){
        const deletedId = result.deleted as string;
        this.onRemoveAllergenGroupFromProduct(deletedId);
        this.deleteFromStore(deletedId);
      }
    });
  }

  async onDeleteAllergenGroup(deletedId: string) {
    if(!this.allowOverrides){
      return;
    }
    if(confirm(this.translate.instant('MENU.ALLERGEN_GROUP.CONFIRM_DELETE'))){
      this.onRemoveAllergenGroupFromProduct(deletedId);
      await this.allergenGroupService.delete(deletedId);
      this.deleteFromStore(deletedId);
    }
  }

  deleteFromStore(deletedId: string) {
    if(!this.allowOverrides){
      return;
    }
    const filtered = [...this.allergenGroups.filter(allergen => allergen.id !== deletedId)];
    this.store.dispatch(setAllergenGroups(filtered));
  }

  isManuallyCreated(allergenGroupId: string) {
    const selected = this.allergenGroupsMap.get(allergenGroupId);
    return selected?.code == null || selected?.externalId == null;
  }
}
